import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getNotifications } from '../services/NotificationService';
import { NotificationResponseModel } from '../types/NotificationModels';
import { parseISO, isToday, isYesterday, format } from 'date-fns';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import {
  Stack,
  Text,
  Button,
  Loader,
  Paper,
  Space,
  ScrollArea,
} from '@mantine/core';
import { useApp } from '../context/AppContext';
import { primaryColor } from '../constants/theme';
import { RenderNotification } from './new/components/notifications/Notification';
import { NoNotifications } from './new/components/notifications/NoNotifications';
import { NotificationSkeleton } from './new/components/notifications/NotificationSkeleton';
import { BookingDecisionModal } from './new/components/bookingDecision/BookingDecisionModal';

export const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<
    NotificationResponseModel[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { token, scrollAreaHeight, isTabletAndBelow } = useApp();
  const [skip, setSkip] = useState(0);
  const size = 30;
  const initialLoad = useRef(true);
  const [bookingDecisionIds, setBookingDecisionIds] = useState<number[] | null>(null);

  const scrollContainerHeight = useMemo(
    () => Number(scrollAreaHeight.replace('px', '')) - 100,
    [scrollAreaHeight]
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      if (token) {
        try {
          const newNotifications = await getNotifications(token, skip, size);
          setNotifications((prevNotifications) => [
            ...prevNotifications,
            ...newNotifications.notifications,
          ]);
        } catch (error) {
          console.error('Error fetching notifications:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (initialLoad.current || skip > 0) {
      initialLoad.current = false;
      fetchNotifications();
    }
  }, [skip]);

  const loadMoreNotifications = () => {
    setSkip((prevSkip) => prevSkip + size);
  };

  const groupedNotifications = useMemo(() => {
    const groups: { label: string; items: NotificationResponseModel[] }[] = [];
    const byLabel: Record<string, NotificationResponseModel[]> = {};

    for (const n of notifications) {
      const date = parseISO(n.createdIso);
      let label: string;
      if (isToday(date)) {
        label = 'Today';
      } else if (isYesterday(date)) {
        label = 'Yesterday';
      } else {
        label = format(date, 'd MMM yyyy');
      }
      if (!byLabel[label]) {
        byLabel[label] = [];
        groups.push({ label, items: byLabel[label] });
      }
      byLabel[label].push(n);
    }

    return groups;
  }, [notifications]);

  return (
    <NavigationWrapper disableScroll>
      <Stack align="center" gap={0} w="100%">
        <Paper
          shadow="none"
          p={{ base: 'md', sm: 'lg' }}
          radius="md"
          withBorder
          maw={1200}
          w="100%"
          mih={isTabletAndBelow ? scrollAreaHeight : 'auto'}
        >
          <ScrollArea h={scrollContainerHeight}>
            <Space h={8} />
            {isLoading && notifications.length === 0 && (
              <Stack gap={8} data-testid="notification-loader">
                {Array.from({ length: 10 }).map((_, index) => (
                  <NotificationSkeleton key={index} />
                ))}
              </Stack>
            )}
            {!isLoading && notifications.length === 0 && <NoNotifications />}
            <Stack gap="md">
              {groupedNotifications.map(({ label, items }) => (
                <Stack key={label} gap="xs">
                  <Text fw={600} size="sm" c="dimmed" ta="left" data-testid={`notification-group-${label}`}>
                    {label}
                  </Text>
                  {items.map((notification) => (
                    <RenderNotification
                      key={notification.id}
                      notification={notification}
                      onBookingRequest={(ids) => setBookingDecisionIds(ids)}
                    />
                  ))}
                </Stack>
              ))}
            </Stack>
            {isLoading && notifications.length > 0 && (
              <Stack align="center" mt="md">
                <Loader size="sm" color={primaryColor} />
              </Stack>
            )}
            {!isLoading && notifications.length >= size && (
              <Stack align="center" mt="md">
                <Button
                  variant="light"
                  color={primaryColor}
                  onClick={loadMoreNotifications}
                  data-testid="button-notifications-load-more"
                >
                  Load more
                </Button>
              </Stack>
            )}
            <Space h={30} />
          </ScrollArea>
        </Paper>
      </Stack>
      <BookingDecisionModal
        opened={bookingDecisionIds !== null}
        onClose={() => setBookingDecisionIds(null)}
        bookingIds={bookingDecisionIds ?? []}
      />
    </NavigationWrapper>
  );
};
